import React, { useState, useEffect } from "react";
import {
  keyBy as _keyBy,
  omitBy as _omitBy,
  isEqual as _isEqual,
} from "lodash";
import { navigate } from "gatsby";
import { Row, Col, List } from "antd";

// Already included by Gatsby
import { useLocation } from "@gatsbyjs/reach-router";
import queryString from "query-string";

import useSearch from "hooks/useSearch";

import SearchBox from "./SearchBox";
import CategorySelect from "./CategorySelect";

import * as styles from "./SearchableList.module.less";

const concatPath = (pathname, searchObject) => {
  let newPath = pathname;
  if (Object.keys(searchObject).length) {
    newPath += "?" + queryString.stringify(searchObject);
  }
  return newPath;
};

/**
 * @template T
 *
 * @typedef {object} SearchCategory
 * @prop {string} [name] Unused for now
 * @prop {(selectedCategory: string, node: T) => boolean} search
 * @prop {readonly string[]} values All categories available
 */

/**
 * @template T
 * @typedef {object} CustomProps
 * @prop {any} index The search index
 * @prop {SearchCategory<T>} [categories]
 * @prop {object} [searchConfig]
 */

/**
 * We allow for every list prop except children
 * @template T
 * @typedef {Omit<import("antd/es/list").ListProps<T>, "children">} ListProps
 */

/**
 * This is just an antd list with search on top
 * This makes handling the layout much simpler
 *
 * @template {{id: string}} T
 *
 * @param {CustomProps<T> & ListProps<T>} props
 */
export default function SearchableList(props) {
  if (typeof window !== "undefined" && window.NETLIFY_CMS) {
    return (
      <SearchableListTemplate
        {...props}
        selectedCategory={""}
        onCategoryChange={() => {}}
        searchText={""}
        onSearch={() => {}}
        results={[]}
        onPageChange={() => {}}
        page={1}
      />
    );
  }
  return <SearchableListComponent {...props} />;
}

const createState = (search) => ({
  searchText: search.q || "",
  selectedCategory: search.category || null,
  page: search.page ? Number(search.page) : 1,
});

/**
 * @template {{id: string}} T
 *
 * @param {CustomProps<T> & ListProps<T>} props
 */
function SearchableListComponent(props) {
  const {
    index,
    categories,
    // 3 props from antd list that we tweak
    dataSource,
    pagination,
    renderItem,
    searchConfig,
    ...otherListProps
  } = props;

  const { pathname, search: searchStr } = useLocation();

  /** @type {Record<string,string>} */
  // @ts-expect-error
  const search = searchStr ? queryString.parse(searchStr) : {};

  const [searchState, setSearchState] = useState(createState(search));

  useEffect(() => {
    const newState = createState(search);

    if (!_isEqual(searchState, newState)) {
      setSearchState(newState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchStr]);

  const setSearch = (newFields) => {
    let newSearch = { ...search, ...newFields };
    newSearch = _omitBy(
      newSearch,
      (val, key) => !val || (key === "page" && val === 1)
    );

    const newState = createState(newSearch);
    setSearchState(newState);

    let newPath = pathname;
    if (Object.keys(newSearch).length) {
      newPath += "?" + queryString.stringify(newSearch);
    }

    const oldPath = concatPath(pathname, search);

    if (newPath !== oldPath) {
      navigate(newPath, {
        replace: true,
      });
    }
  };

  const { searchText, page, selectedCategory } = searchState;

  const itemsById = _keyBy(dataSource, "id");
  const resultsIds = useSearch(searchText, index, searchConfig);

  let results = searchText ? resultsIds.map((id) => itemsById[id]) : dataSource;

  if (categories && selectedCategory) {
    results = results.filter((node) =>
      categories.search(selectedCategory, node)
    );
  }

  return (
    <SearchableListTemplate
      categories={categories}
      renderItem={renderItem}
      pagination={pagination}
      selectedCategory={selectedCategory}
      onCategoryChange={(newCategory) => {
        setSearch({
          page: 1,
          category: newCategory,
        });
      }}
      searchText={searchText}
      onSearch={(newQuery) => setSearch({ page: 1, q: newQuery })}
      results={results}
      page={page}
      onPageChange={(page) => setSearch({ page })}
      {...otherListProps}
    />
  );
}

/**
 * @template {{id: string}} T
 * @typedef {object} AdditionalProps
 * @prop {string} selectedCategory
 * @prop {string} searchText
 * @prop {number} page
 * @prop {any[]} results
 * @prop {SearchCategory<T>} [categories]
 * @prop {() => void} onCategoryChange
 * @prop {() => void} onSearch
 * @prop {() => void} onPageChange
 */

/**
 * @template {{id: string}} T
 * @param {AdditionalProps<T> & ListProps<T>} props
 */
function SearchableListTemplate(props) {
  const {
    selectedCategory,
    onCategoryChange,
    searchText,
    onSearch,
    results,
    page,
    onPageChange,
    categories,
    renderItem,
    pagination,
    ...otherListProps
  } = props;

  return (
    <section className={styles.container}>
      <Row className={styles.header} gutter={24}>
        {categories && (
          <Col xs={24} lg={12} className={styles.headerBox}>
            <CategorySelect
              categories={categories}
              value={selectedCategory}
              onChange={onCategoryChange}
            />
          </Col>
        )}
        <Col xs={24} lg={categories ? 12 : 24} className={styles.headerBox}>
          <SearchBox query={searchText} onSearch={onSearch} />
        </Col>
      </Row>
      <List
        dataSource={results.filter((item) => Boolean(item))}
        // Needed for search anyways
        rowKey="id"
        // Default pagination settings. Can be disabled/edited by the upper component
        pagination={{
          // @ts-expect-error
          current: parseInt(page, 10),
          position: "bottom",
          defaultPageSize: 10,
          showSizeChanger: false,
          hideOnSinglePage: true,
          onChange: onPageChange,
          ...pagination,
        }}
        renderItem={(item, index) => (
          <List.Item>{renderItem(item, index)}</List.Item>
        )}
        locale={{
          emptyText: "No result",
        }}
        {...otherListProps}
      />
    </section>
  );
}
